import type { Zone } from '../janus-engine/inventory/floor-move.js';
/** A non-null document gives surface anchors the existing generated cast scope. No schema change. */
export interface SurfaceGeometry {
  schema: 'ft.surface.v1';
  sourceInventoryId?: string;
  sourceStatusId?: string;
  sourceConnectionId?: string;
  footprint?: Zone;
}
export function parseSurfaceGeometry(raw: unknown): SurfaceGeometry | null {
  if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { return null; } }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  if (row.schema !== 'ft.surface.v1') return null;
  if (row.sourceInventoryId != null && (typeof row.sourceInventoryId !== 'string'
    || !/^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/i.test(row.sourceInventoryId))) return null;
  if (row.sourceStatusId != null && (typeof row.sourceStatusId !== 'string' || !/^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/i.test(row.sourceStatusId))) return null;
  if (row.sourceConnectionId != null && (typeof row.sourceConnectionId !== 'string' || !/^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/i.test(row.sourceConnectionId))) return null;
  const footprint = row.footprint as Zone | undefined;
  if (footprint && (!(footprint.kind === 'circle' || footprint.kind === 'oblong')
    || ![footprint.x, footprint.y, footprint.zMin, footprint.zMax, ...(footprint.kind === 'circle'
      ? [footprint.r] : [footprint.halfW, footprint.halfD, footprint.facing])].every(Number.isFinite))) return null;
  return { schema: 'ft.surface.v1', ...(row.sourceInventoryId ? { sourceInventoryId: String(row.sourceInventoryId).toUpperCase() } : {}),
    ...(row.sourceStatusId ? { sourceStatusId: String(row.sourceStatusId).toUpperCase() } : {}),
    ...(row.sourceConnectionId ? { sourceConnectionId: String(row.sourceConnectionId).toUpperCase() } : {}),
    ...(footprint ? { footprint } : {}) };
}
export const hasSurfaceTypeValue = (value: unknown): boolean => typeof value === 'string' && value.trim() !== '';
/** Surface contact includes a body standing on the patch's floor; physical obstacle overlap excludes that contact. */
export function surfaceFootprintsContact(a: Zone, b: Zone): boolean {
  const epsilon = 1e-6;
  if (a.zMax < b.zMin - epsilon || b.zMax < a.zMin - epsilon) return false;
  if (a.kind === 'circle' && b.kind === 'circle') return Math.hypot(a.x - b.x, a.y - b.y) <= a.r + b.r + epsilon;
  if (a.kind === 'circle' || b.kind === 'circle') {
    const circle = (a.kind === 'circle' ? a : b) as Extract<Zone, { kind: 'circle' }>;
    const box = (a.kind === 'oblong' ? a : b) as Extract<Zone, { kind: 'oblong' }>;
    const dx = circle.x - box.x, dy = circle.y - box.y, c = Math.cos(box.facing), s = Math.sin(box.facing);
    return Math.hypot(Math.max(0, Math.abs(dx * c + dy * s) - box.halfW),
      Math.max(0, Math.abs(-dx * s + dy * c) - box.halfD)) <= circle.r + epsilon;
  }
  const dx = b.x - a.x, dy = b.y - a.y;
  return [a.facing, a.facing + Math.PI / 2, b.facing, b.facing + Math.PI / 2].every(angle => {
    const span = (box: Extract<Zone, { kind: 'oblong' }>) => box.halfW * Math.abs(Math.cos(angle - box.facing))
      + box.halfD * Math.abs(Math.sin(angle - box.facing));
    return Math.abs(dx * Math.cos(angle) + dy * Math.sin(angle)) <= span(a) + span(b) + epsilon;
  });
}
export const SURFACE_LOOK_STATUS_IDS: Readonly<Record<string, string>> = {
  ice: '6EFBE105-7632-4C64-BC04-206CEAF663E6', electrified: 'E5452E0D-251E-4BDD-AC25-DADD00A7BEC7',
};
